import { Product } from '../product.interface';

export const MULTI_BAG_REDUCE_STORAGE_RATES_MODIFIER_KEY = 'MULTI_BAG_REDUCE_STORAGE_RATES';
export const MULTI_DAY_REDUCE_STORAGE_RATES_MODIFIER_KEY = 'MULTI_DAY_REDUCE_STORAGE_RATES';
export const HONEYMOON_REDUCE_RATES_MODIFIER_KEY = 'HONEYMOON_REDUCE_RATES';
/** Discount on the advance storage rates for a booking made before the drop-off day (AM-4961). */
export const ADVANCE_BOOKING_DISCOUNT_MODIFIER_KEY = 'ADVANCE_BOOKING_DISCOUNT';

/**
 * @deprecated Use `OrderLine` instead
 */
export interface LegacyOrderLine {
  product: Product;
  quantity: number;
  showQuantity?: boolean;
  showQuantityAsUpTo?: boolean;
  unitPrice: number;
  showUnitPrice?: boolean;
  optional?: boolean;
  selected?: boolean;
  isRelative?: boolean;
  isPricing: boolean;
  pricingSuffix?: string;
  priority?: number;
  total?: number;
  /**
   * Render this line for assistive technology only — present in the accessibility tree, absent from
   * the page (AM-4788).
   *
   * Used by the free-cancellation line. The pre-booking summaries and manage-booking no longer show
   * it: the panel states the policy once, in "More info". But that window is a tooltip with no
   * `tabindex`, role or `aria-describedby`, so a keyboard or screen-reader user cannot reach it —
   * dropping the line outright would leave them with no deadline at all. Hiding it visually keeps
   * the promise where they can still read it.
   *
   * The cancel dialog does not set this: there the deadline is what the traveler is acting on, so
   * it stays visible to everyone.
   */
  visuallyHidden?: boolean;
  /**
   * How many chargeable periods (days, or hours) this line's `unitPrice` covers, for storage lines
   * (AM-4637).
   *
   * The conversion in `SharedOrderService` folds the period count into `unitPrice` — a daily line for a
   * 3-day stay carries `dailyRate * 3` — because that is what the order summary needs to render. That
   * makes `unitPrice` unusable as a *rate*: anything wanting "per day" has to divide it back out, and the
   * count was previously discarded, so callers could not. Recording it here keeps the per-period rate
   * recoverable (`SharedUtilOrder.perBagStoragePeriodRate`) without re-deriving the period from dates and
   * risking a different answer than the server's.
   *
   * Absent or 0 means "no multiplier applied" — treat it as 1.
   */
  storagePeriods?: number;
  /**
   * Set on a storage savings line (a volume, multi-day or advance booking discount shown on its own line):
   * the product key of the storage line it discounts (AM-4961).
   *
   * The order summary splits such a discount out, adding it back onto the storage line so that line shows
   * the full rate. Anything reading the storage line as "the rate the traveler pays" must therefore net
   * these lines back in — `SharedUtilOrder.perBagStoragePeriodRate` and `storageUnitPrices` do.
   */
  discountsStorageProductKey?: string;
}

export interface OrderLine {
  name: string; // Storage Day
  productKey: string;
  productId: string;
  units: number; // 3
  unitPrice: number; // 10
  price: number; // 30
  modifiedPrice?: ModifiedOrderLineInfo[];
  finalPrice: number;
  priceDelta: number;
}

export interface ModifiedOrderLineInfo {
  priceModifierId: string;
  priceModifierName: string;
  priceModifierKey: string; // for translations
  unitPrice: number;
  price: number; // 25
  priceDelta: number; // -5  if negative then discounted
  description?: string; // Multi Day Discount
}
